import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { NavigationEnd, Router, RouterModule, RouterOutlet } from "@angular/router";
import { MatFormField } from "@angular/material/form-field";
import { MatSelectModule } from '@angular/material/select';

import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AuthService } from '@auth/services/auth.service';
import { Languages } from '@enums/languages';
import { Menu } from '@auth/models/menu.model';
import { ThemeService } from '@sharedServices/theme.service';
import packageJson from '../../../../package.json'
import { PermissionEnum } from '@auth/enums/permission.enum';
import { RoleMenuPermissionService } from '@userModule/services/role-menu-permission.service';
import { CanAccess } from '@auth/directives/can-access';
import { WindowsID } from '@enums/windows.enum';
import { filter } from 'rxjs';

/**
 * The main layout for the application content.
 */
@Component({
  selector: 'app-content-layout',
  imports: [
    MatSidenavModule,
    MatButtonModule,
    MatIconModule,
    MatToolbarModule,
    MatMenuModule,
    MatExpansionModule,
    RouterOutlet,
    TranslateModule,
    MatFormField,
    MatSelectModule,
    RouterModule,
    CanAccess
],
providers: [ThemeService],
  templateUrl: './content-layout.html',
  styleUrl: './content-layout.scss'
})
export class ContentLayout implements OnInit {
  themeService = inject(ThemeService);
  @ViewChild('drawer') drawer!: MatDrawer;

  protected translateService = inject(TranslateService);
  protected menuItems: Menu[] = [];
  protected year = new Date().getFullYear();
  protected systemVersion: string = packageJson.version;
  protected permissionEnum = PermissionEnum;
  protected windowsId = WindowsID;
  protected languages = Object.entries(Languages).map(([code, label]) => ({
    code: code.toLocaleLowerCase(),
    label: label
  }));

  private _authService = inject(AuthService);
  private _roleMenuPermissionService = inject(RoleMenuPermissionService);
  private _router: Router = inject(Router);

  /**
   * Initializes the component, fetches the menu, and sets up navigation event listeners.
   */
  ngOnInit(): void {
    this._getMenu();

    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this._closeSidebarOnNavigation();
    });
  }

  /**
   * Logs out the current user.
   * @protected
   */
  protected logout(): void {
    this._authService.logout().subscribe();
  }

  /**
   * Changes the application language.
   * @param lang - The language to switch to.
   * @protected
   */
  protected changeLanguage(lang: string): void {
    localStorage.setItem('lang', lang);
    this.translateService.use(lang);
  }

  /**
   * Retrieves the menu for the current user's role.
   * @private
   */
  private _getMenu(): void {
    this._roleMenuPermissionService.findMenuByRoleId(this._authService.user()!.role.id).subscribe({
      next: (res) => {
        if (res.success) {
          this.menuItems = res.payload;
        }
      }
    });
  }

  /**
   * Closes the sidebar drawer if it is currently open.
   * @private
   */
  private _closeSidebarOnNavigation(): void {
    if (this.drawer && this.drawer.opened) {
      this.drawer.close();
    }
  }
}
